/**
 * @fileoverview Job Management Hooks
 * 
 * Core hooks for fetching and managing job data throughout the application.
 * Handles job listing with advanced filtering, single job fetching with full details,
 * and integration with RFQ expiration management.
 * 
 * Jobs represent procurement requests from clients, containing:
 * - Basic info (title, description, budget, category)
 * - Associated RFQ details (quotes, vendors, deadlines)
 * - Award information (if job has been awarded)
 * - Specifications and delivery requirements
 * 
 * @module hooks/useJobs
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { Job, JobStatus, JobCategory } from '@/lib/types/job';

/**
 * Options for filtering and controlling the useJobs hook
 */
interface UseJobsOptions {
  /** Filter jobs by status (e.g., 'draft', 'open', 'awarded') */
  status?: JobStatus[];
  /** Filter jobs by category */
  category?: JobCategory[];
  /** Search jobs by title or description */
  search?: string;
  /** Filter jobs with budget greater than or equal to this value (in cents) */
  budgetMin?: number;
  /** Filter jobs with budget less than or equal to this value (in cents) */
  budgetMax?: number;
  /** Whether to enable fetching (default: true) */
  enabled?: boolean;
  /** Include organization details in the query (for admin context) */
  includeOrganization?: boolean;
}

/**
 * Hook for fetching and managing multiple jobs with filtering options
 * 
 * @param options - Filtering and control options
 * @returns Object containing jobs array, loading state, error, and refetch function
 * 
 * @example
 * ```typescript
 * const { jobs, loading, error, refetch } = useJobs({
 *   status: ['open', 'awarded'],
 *   category: ['print_materials'],
 *   budgetMin: 100000, // $1000 in cents
 * });
 * ```
 */
export const useJobs = (options: UseJobsOptions = {}) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = async () => {
    try {
      if (options.enabled === false) {
        setLoading(false);
        return;
      }
      setLoading(true);

      // Ensure expired RFQs are closed before fetching jobs
      try {
        await invokeEdgeFunction('close-expired-rfqs', {
          callerOperation: 'useJobs.fetchJobs',
        });
      } catch (e) {
        console.warn('close-expired-rfqs invocation failed (non-blocking):', e);
      }
      
      // Build select query with optional organization join for admin context
      const baseSelect = `
        *,
        rfq:rfqs(
          *,
          bids(id)
        ),
        awards!left(
          id,
          winning_bid_id,
          total_award_amount_cents,
          award_selections(*)
        ),
        purchase_order:purchase_orders!left(
          id,
          po_amount_cents,
          status,
          updated_at,
          vendor_organization_id
        )
      `;
      
      const selectQuery = options.includeOrganization 
        ? `${baseSelect}, organization:organizations!jobs_organization_id_fkey(id, name)`
        : baseSelect;

      let query = supabase
        .from('jobs')
        .select(selectQuery)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false });

      // Apply filters
      if (options.status && options.status.length > 0) {
        query = query.in('status', options.status);
      }

      if (options.category && options.category.length > 0) {
        query = query.in('category', options.category);
      }

      if (options.budgetMin !== undefined) {
        query = query.gte('budget_cents', options.budgetMin);
      }

      if (options.budgetMax !== undefined) {
        query = query.lte('budget_cents', options.budgetMax);
      }

      if (options.search) {
        query = query.textSearch('title,description', options.search);
      }

      const { data: jobsData, error } = await query;

      if (error) throw error;

      // Fetch client profiles separately (jobs.created_by_user_id -> client_profiles.user_id)
      let enrichedJobs = jobsData || [];
      if (jobsData && jobsData.length > 0) {
        const creatorIds = [...new Set(jobsData.map((j: any) => j.created_by_user_id).filter(Boolean))];
        
        // Collect vendor organization IDs from purchase orders
        const vendorOrgIds = [...new Set(
          jobsData
            .map((j: any) => j.purchase_order?.vendor_organization_id)
            .filter(Boolean)
        )];
        
        // Fetch client profiles and vendor orgs in parallel
        const [clientProfilesResult, vendorOrgsResult] = await Promise.all([
          creatorIds.length > 0 
            ? supabase
                .from('client_profiles')
                .select('user_id, first_name, last_name')
                .in('user_id', creatorIds)
            : Promise.resolve({ data: [] }),
          vendorOrgIds.length > 0
            ? supabase
                .from('organizations')
                .select('id, name, vendor_profile:vendor_profiles_enhanced(business_name)')
                .in('id', vendorOrgIds)
            : Promise.resolve({ data: [] })
        ]);
        
        const profileMap = new Map<string, any>(clientProfilesResult.data?.map(p => [p.user_id, p] as [string, any]) || []);
        const vendorOrgMap = new Map<string, any>(vendorOrgsResult.data?.map(o => [o.id, o] as [string, any]) || []);
        
        enrichedJobs = jobsData.map((job: any) => ({
          ...job,
          creator: job.created_by_user_id ? profileMap.get(job.created_by_user_id) || null : null,
          purchase_order: job.purchase_order ? {
            ...job.purchase_order,
            vendor_organization: vendorOrgMap.get(job.purchase_order.vendor_organization_id) || null
          } : null
        }));
      }

      setJobs(enrichedJobs as unknown as Job[] || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (options.enabled === false) {
      setLoading(false);
      return;
    }
    fetchJobs();
  }, [options.enabled, options.status, options.category, options.search, options.budgetMin, options.budgetMax]);

  // Remove realtime subscription for job lists - use manual refetch instead
  // Realtime is too expensive for table-wide updates with 3.8M+ queries
  // Individual job pages can still use realtime if needed

  const refetch = () => fetchJobs();

  return { jobs, loading, error, refetch };
};

/**
 * Hook for fetching a single job by its public ID
 * 
 * @param jobId - The public ID of the job to fetch
 * @returns Object containing job data, loading state, and error
 * 
 * @example
 * ```typescript
 * const { job, loading, error } = useJob('abc-123-def');
 * if (!loading && job) {
 *   console.log(job.title);
 * }
 * ```
 */
export const useJob = (jobId: string) => {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJob = async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      
      // Ensure expired RFQs are closed before fetching this job
      try {
        await invokeEdgeFunction('close-expired-rfqs', {
          callerOperation: 'useJob.fetchJob',
        });
      } catch (e) {
        console.warn('close-expired-rfqs invocation failed (non-blocking):', e);
      }
      
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          rfq:rfqs(
            *,
            addenda:rfq_addenda(*),
            questions:rfq_questions(*),
            acknowledgments:vendor_rfq_acknowledgments(*)
          )
        `)
        .eq('public_id', jobId)
        .eq('is_deleted', false)
        .maybeSingle();

      if (error) throw error;

      setJob(data as any);
      setError(null);
    } catch (err) {
      console.error('Error fetching job:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch job');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const refetch = () => fetchJob();

  return { job, loading, error, refetch };
};